import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import type { User } from '@supabase/supabase-js';
import { supabase, AUTH_STORAGE_KEY, readStoredValue, removeStoredGroup } from '@/lib/supabase';
import { normalizeAppRole } from '@/lib/roles';
import { setActionLogUser, logUserAction } from '@/lib/actionLog';
import { clearSignedUrlCache } from '@/lib/storageSignedUrl';

interface Profile {
  id: string;
  username: string;
  email: string;
  role: 'welfare_user' | 'welfare_staff' | 'welfare_admin' | 'welfare_supervisor' | 'general_user' | 'general_admin';
  office_id: string;
  /** 複数所属事業所ID（総合管理者・福祉事業管轄で使用）。設定系の対象事業所プルダウンで使う。 */
  office_ids?: string[] | null;
  phone?: string;
  furigana?: string;
  departments?: string[] | null;
  /** ユーザーごとの文字サイズ設定（未設定は null）。初回ポップアップで必須設定する。 */
  font_scale?: string | null;
  /** IT管理者フラグ。ロール非依存（どのロールにも付与可）。 */
  is_it_admin?: boolean;
  /** IT開発者フラグ。ロール非依存（どのロールにも付与可）。 */
  is_it_developer?: boolean;
  /** 有効なアカウントか。false は「無効化（退職など）」。ログインさせない。 */
  is_active?: boolean;
  /** 事業所（offices）の埋め込み。プロフィール取得時に一緒に取得する */
  offices?: { name: string; theme_color: string | null } | null;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  /** profile に含まれる事業所名（未ロード時は空文字） */
  officeName: string;
  /** profile に含まれる事業所テーマカラー（未設定・未ロード時は空文字） */
  officeThemeColor: string;
  loading: boolean;
  /** ログアウト遷移中かどうか（画面全体をフェードで覆うオーバーレイ表示に使う） */
  loggingOut: boolean;
  /** ログアウト用オーバーレイのアニメーション終了時に呼ぶ */
  endLogout: () => void;
  signOut: () => Promise<void>;
  /** プロフィールを再取得する（文字サイズ設定などの更新を反映する） */
  refreshProfile: () => Promise<void>;
}

/**
 * オンラインなら指数バックオフで待機。オフラインなら接続復帰イベントを待つ（最大20秒）。
 * 起動時のセッション確認が一時的なネットワーク失敗で終わらないよう、粘るために使う。
 */
function waitForRetry(attempt: number): Promise<void> {
  const delay = 400 * Math.pow(2, attempt - 1); // 400, 800, 1600 ...
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && !navigator.onLine) {
      const cleanup = () => {
        window.removeEventListener('online', onOnline);
        clearTimeout(timer);
      };
      const onOnline = () => {
        cleanup();
        resolve();
      };
      const timer = setTimeout(() => {
        cleanup();
        resolve();
      }, 20000);
      window.addEventListener('online', onOnline);
      return;
    }
    setTimeout(resolve, delay);
  });
}

/**
 * localStorage に保存された Supabase セッションから user を楽観的に読み出す（ネットワーク確認なし）。
 * 起動時に通信が失敗しても、保存済みセッションがあるならログイン画面へ弾かないための保険。
 */
function readPersistedUser(): User | null {
  try {
    const candidates: string[] = [];
    // 現行の固定キー（URLに依存しない）
    if (readStoredValue(AUTH_STORAGE_KEY) !== null) candidates.push(AUTH_STORAGE_KEY);
    // 旧キー（移行前の残り。固定キーが無いときの保険）
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) candidates.push(key);
    }
    for (const key of candidates) {
      const raw = readStoredValue(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const u = parsed?.user ?? parsed?.session?.user;
      if (u && u.id) return u as User;
    }
  } catch {
    // 解析できなくても致命的ではない
  }
  return null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const isMountedRef = useRef(true);
  const isLoggingOutRef = useRef(false);
  // 復帰時のセッション再確認を連発させないための最終実行時刻（スロットル用）
  const lastRecheckRef = useRef(0);
  // 現在ログイン状態かどうか。復帰時に「ログイン中なのにセッションが無い」ケースだけ
  // ログアウト判定へ進めるよう、誤爆を避けるための判定に使う。
  const hasUserRef = useRef(false);
  // ログアウト記録用に、現在のユーザーIDを保持する（signOut のコールバック依存を増やさない）。
  const profileIdRef = useRef<string | null>(null);

  // プロフィール取得（例外を投げず、失敗時はnullを返すだけ。
  // 単発の取得失敗で強制ログアウトさせないことが重要）
  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        // profiles と offices の間には2本のFK（office_id / active_office_id）があるため、
        // 主所属の関係を FK 名で明示して曖昧さを解消する。
        // 明示しないと "more than one relationship was found" で取得が失敗する。
        .select('*, offices!profiles_office_id_fkey(name, theme_color)')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('プロフィール取得エラー:', error.message);
        return null;
      }
      if (!data) return null;

      // 無効化されたアカウント（退職など）はログインさせない。
      // auth.users は残しているため、ここで is_active=false を検出してセッションを破棄し、
      // ログイン画面で理由を表示できるようフラグを立てる（プロフィールは返さない）。
      if ((data as { is_active?: boolean | null }).is_active === false) {
        try {
          sessionStorage.setItem('account_disabled', '1');
        } catch {
          // ストレージが使えない環境は無視
        }
        void supabase.auth.signOut({ scope: 'local' });
        return null;
      }

      // ロール再編の移行途中でも判定がぶれないよう、旧ロール値（user / admin / super_admin）は
      // 新ロール値へ正規化して保持する（RLS・ai-manual-qa は既に正規化しているため、フロントだけ生値を見て食い違うのを防ぐ）。
      return {
        ...data,
        role: normalizeAppRole((data as { role?: string | null }).role) as Profile['role'],
      } as Profile;
    } catch (error) {
      console.error('プロフィール取得例外:', error);
      return null;
    }
  }, []);

  // プロフィール取得のリトライ版。
  // デプロイ直後など起動直後に一時的な通信失敗が起きても、
  // 「プロフィール未取得」のままログアウト扱い（＝ログイン画面表示）にならないよう数回粘る。
  const fetchProfileWithRetry = useCallback(
    async (userId: string, maxAttempts = 3): Promise<Profile | null> => {
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const data = await fetchProfile(userId);
        if (data) return data;
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
        }
      }
      console.warn('[AUTH] プロフィール取得に失敗しました（セッションは維持します）');
      return null;
    },
    [fetchProfile]
  );

  // SupabaseがlocalStorageに保存するすべてのセッション情報を完全削除
  const clearAllSupabaseStorage = useCallback(() => {
    // 非公開バケットの署名付きURLキャッシュは、セッション終了時に必ず破棄する
    // （ログアウト後もブラウザのメモリ上に有効な URL が残らないようにする）
    clearSignedUrlCache();
    try {
      // 固定キー（＋分割チャンク）を確実に削除
      removeStoredGroup(AUTH_STORAGE_KEY);
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('sb-') ||
          key.startsWith('supabase.') ||
          key.includes('supabase')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // クリアできなくても先に進む
    }
  }, []);

  // 意図しないサインアウト（トークン更新失敗など）の受け流し。
  // セッションがまだ生きているかを確認してからログアウトを確定する。
  // ※ onAuthStateChange のコールバック内では同期的に呼ばず、遅延させて実行する。
  const verifyAndHandleSignOut = useCallback(async () => {
    if (!isMountedRef.current) return;

    let sessionUser: User | null = null;
    try {
      // まず現在のセッションを確認（同時更新の片方が成功していれば残っている）
      const { data } = await supabase.auth.getSession();
      if (data?.session?.user) {
        sessionUser = data.session.user;
      } else {
        // まだ復帰の余地があるなら明示的にリフレッシュを試みる
        const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError && refreshed?.session?.user) {
          sessionUser = refreshed.session.user;
        }
      }
    } catch {
      // 判定に失敗（オフライン等）した場合は、誤ログアウトを避けるため何もしない
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    }

    if (sessionUser) {
      // 復帰できた → ログイン状態を維持し、プロフィールも取り直す
      if (isMountedRef.current) setUser(sessionUser);
      const profileData = await fetchProfileWithRetry(sessionUser.id);
      if (isMountedRef.current && profileData) setProfile(profileData);
      return;
    }

    // ネットワーク起因の可能性があるときは、この時点では確定させない
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    // 本当にセッションが失効している → ログアウト（保存クリア＋ログイン画面へ）
    if (isMountedRef.current) {
      setUser(null);
      setProfile(null);
    }
    clearAllSupabaseStorage();
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/login', { replace: true });
    } else {
      window.location.href = '/login';
    }
  }, [fetchProfile, fetchProfileWithRetry, clearAllSupabaseStorage]);

  // アプリ復帰時（タブ/アプリが再び可視になった瞬間）に、ユーザーに何も見せずに
  // セッションを静かに再確認する。スリープ中は autoRefresh のタイマーが止まっていることがあり、
  // 復帰直後にトークンが期限切れのままになっている「取りこぼし」をここで回収する。
  const silentSessionRecheck = useCallback(async () => {
    if (!isMountedRef.current || isLoggingOutRef.current) return;
    // オフラインでは何もできないので、復帰後にオンラインイベントで拾う
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    // 可視化・フォーカスで連発するため、短時間の再実行は間引く
    const now = Date.now();
    if (now - lastRecheckRef.current < 5000) return;
    lastRecheckRef.current = now;

    try {
      const { data, error } = await supabase.auth.getSession();
      // 取得自体の失敗（弱電波・タイムアウト）は一時的なものとみなして何もしない
      if (error) return;

      const session = data?.session;
      if (session?.user) {
        // 期限切れ、または残り時間が少ないときだけ静かに更新する
        const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
        const shouldRefresh = expiresAt > 0 && expiresAt - now < 60_000;
        if (shouldRefresh) {
          const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshed?.session?.user && isMountedRef.current) {
            setUser(refreshed.session.user);
          }
        }
        return;
      }

      // セッションが無い：ログイン中だった場合のみ、本当に失効したか確認してから確定する
      if (hasUserRef.current) {
        void verifyAndHandleSignOut();
      }
    } catch {
      // 判定に失敗（オフライン等）した場合は、誤ログアウトを避けるため何もしない
    }
  }, [verifyAndHandleSignOut]);

  useEffect(() => {
    isMountedRef.current = true;

    // 起動時のセッション確認。ネットワークの一時的な失敗（移動中・スリープ復帰直後・弱電波での
    // タイムアウトなど）で誤ってログアウトさせないよう、粘って確認する。
    const initializeAuth = async () => {
      const MAX_ATTEMPTS = 3;

      const applySession = async (sessionUser: User) => {
        if (isMountedRef.current) {
          setUser(sessionUser);
        }
        // プロフィール取得完了まで loading を維持する。
        // これにより、ダッシュボード側の権限チェックが profile 未ロードのまま
        // 走って管理者を一般ユーザーと誤判定するのを防ぐ。
        const profileData = await fetchProfileWithRetry(sessionUser.id);
        if (isMountedRef.current) {
          if (profileData) {
            setProfile(profileData);
          }
          setLoading(false);
        }
      };

      const goLoggedOut = () => {
        if (isMountedRef.current) {
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      };

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        try {
          const { data: { session } } = await supabase.auth.getSession();

          if (session?.user) {
            await applySession(session.user);
          } else {
            goLoggedOut();
          }
          return;
        } catch (error: any) {
          const errorMsg: string = error?.message || '';
          const isInvalidToken =
            errorMsg.includes('Invalid Refresh Token') ||
            errorMsg.includes('Refresh Token Not Found') ||
            errorMsg.includes('refresh_token') ||
            errorMsg.includes('session_not_found');

          // セッションが本当に失効・破損している場合のみクリーンアップしてログアウト。
          if (isInvalidToken) {
            console.warn('セッションが期限切れまたは破損しています。クリーンアップします。');
            clearAllSupabaseStorage();
            // SDK側の内部状態もリセット（失敗しても無視）
            try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* クリーンアップ失敗は無視 */ }
            goLoggedOut();
            return;
          }

          // それ以外（オフライン・タイムアウト・504 など）は一時的な失敗とみなして再試行する。
          console.warn(`セッション確認に失敗（${attempt}/${MAX_ATTEMPTS}）:`, errorMsg || error);
          if (attempt < MAX_ATTEMPTS) {
            await waitForRetry(attempt);
            continue;
          }

          // 最後まで判定できなかった場合は、誤ログアウトを避けるため
          // 保存済みセッションからの復元を試みる。
          const persisted = readPersistedUser();
          if (persisted) {
            await applySession(persisted);
            return;
          }
          // 保存済みセッションも無い場合のみログアウト扱いにする。
          goLoggedOut();
          return;
        }
      }
    };

    initializeAuth();

    // onAuthStateChangeのコールバックは同期的に保つ（内部ロックのデッドロック回避）
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMountedRef.current) return;

      if (event === 'SIGNED_OUT') {
        if (isLoggingOutRef.current) {
          // 手動ログアウト：即座に状態をクリアする
          setUser(null);
          setProfile(null);
          clearAllSupabaseStorage();
          return;
        }
        // トークン更新の失敗などで発火した「意図しないサインアウト」は、
        // 本当にセッションが無くなったか確認してからにする。
        // ※ コールバック内で同期的に認証APIを叩かないよう、次のタスクへ遅延させる
        //   （同時更新の片方が成功しているケースを拾って、誤ログアウトを防ぐ）
        setTimeout(() => {
          void verifyAndHandleSignOut();
        }, 120);
        return;
      }

      // トークン更新に失敗した場合もクリーンアップ
      if (event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser(session.user);
          fetchProfile(session.user.id).then((profileData) => {
            if (isMountedRef.current && profileData) {
              setProfile(profileData);
            }
          });
        }
        return;
      }

      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id).then((profileData) => {
          if (isMountedRef.current && profileData) {
            setProfile(profileData);
          }
        });
      }
    });

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile, fetchProfileWithRetry, clearAllSupabaseStorage, verifyAndHandleSignOut]);

  // ログイン状態ミラーを更新（復帰時の誤ログアウト判定で使う）
  useEffect(() => {
    hasUserRef.current = !!user;
  }, [user]);

  // アクションログの記録者IDをキャッシュする（各操作のログで getSession の往復を省く）。
  useEffect(() => {
    profileIdRef.current = profile?.id ?? null;
    setActionLogUser(profile?.id ?? null);
  }, [profile]);

  // アプリ復帰を検知して、セッションを静かに再確認する。
  // visibilitychange: タブ/アプリが再び可視になった（他アプリから戻った等）
  // pageshow: bfcache からの復帰
  // focus: ウィンドウが再フォーカスされた
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        void silentSessionRecheck();
      }
    };
    const handleResume = () => {
      void silentSessionRecheck();
    };

    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('pageshow', handleResume);
    window.addEventListener('focus', handleResume);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('pageshow', handleResume);
      window.removeEventListener('focus', handleResume);
    };
  }, [silentSessionRecheck]);

  const signOut = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    // 先に全画面のフェードオーバーレイを表示し、画面を覆ってから
    // 状態クリアとログイン画面への遷移を行うことで、切り替えを滑らかにする。
    // ログアウト記録は、セッションが生きているうちにフェードと並行して送る。
    setLoggingOut(true);
    await Promise.all([
      logUserAction({
        actionType: 'logout',
        targetType: 'auth',
        userId: profileIdRef.current ?? undefined,
      }),
      new Promise((resolve) => setTimeout(resolve, 320)),
    ]);

    flushSync(() => {
      setUser(null);
      setProfile(null);
    });

    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
    clearAllSupabaseStorage();

    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/login', { replace: true });
    } else {
      window.location.href = '/login';
    }

    setTimeout(() => {
      isLoggingOutRef.current = false;
      setLoggingOut(false);
    }, 1000);
  }, [clearAllSupabaseStorage]);

  const endLogout = useCallback(() => {
    setLoggingOut(false);
  }, []);

  // プロフィールを再取得する（文字サイズなどの本人設定の更新を画面へ反映する）
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const data = await fetchProfile(user.id);
    if (isMountedRef.current && data) {
      setProfile(data);
    }
  }, [user, fetchProfile]);

  // 事業所名・テーマカラーは profile 取得時に一緒に取得済み。
  // これにより各ページが初回描画から正しい事業所名・テーマ色で表示できる
  // （別途フェッチして後から色・名前が切り替わる「ちらつき」を防ぐ）。
  const officeName = useMemo(() => profile?.offices?.name ?? '', [profile]);
  const officeThemeColor = useMemo(() => profile?.offices?.theme_color ?? '', [profile]);

  return (
    <AuthContext.Provider value={{ user, profile, officeName, officeThemeColor, loading, loggingOut, endLogout, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}